import { firebaseConfig } from '../config/firebase';

type AuthUser = {
  uid: string;
  isAnonymous: boolean;
};

type AuthState = {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
};

let authInstance: any = null;
let authState: AuthState = {
  user: null,
  loading: true,
  error: null,
};

const authListeners: ((state: AuthState) => void)[] = [];

// Initialize Firebase Auth
async function initializeAuth() {
  if (authInstance) return authInstance;
  
  try {
    const { getAuth, onAuthStateChanged } = await import('firebase/auth');
    const { initializeApp, getApps } = await import('firebase/app');
    
    // Initialize Firebase app if not already done
    if (!getApps().length) {
      initializeApp(firebaseConfig);
    }
    
    authInstance = getAuth();
    
    // Set up auth state listener
    onAuthStateChanged(authInstance, (user) => {
      authState = {
        user: user ? {
          uid: user.uid,
          isAnonymous: user.isAnonymous,
        } : null,
        loading: false,
        error: null,
      };
      
      // Notify all listeners
      authListeners.forEach(listener => {
        try {
          listener(authState);
        } catch (error) {
          console.error('Auth listener error:', error);
        }
      });
    });
    
    return authInstance;
  } catch (error) {
    console.error('Failed to initialize auth:', error);
    authState = {
      user: null,
      loading: false,
      error: 'Failed to initialize authentication',
    };
    return null;
  }
}

// Sign in anonymously
export async function signInAnonymously(): Promise<AuthUser | null> {
  try {
    const auth = await initializeAuth();
    if (!auth) return null;
    
    const { signInAnonymously: signInAnon } = await import('firebase/auth');
    const result = await signInAnon(auth);
    
    return {
      uid: result.user.uid,
      isAnonymous: result.user.isAnonymous,
    };
  } catch (error) {
    console.error('Anonymous sign-in failed:', error);
    authState.error = 'Failed to sign in anonymously';
    return null;
  }
}

// Get current user
export function getCurrentUser(): AuthUser | null {
  return authState.user;
}

// Subscribe to auth state changes
export function onAuthStateChange(callback: (state: AuthState) => void): () => void {
  authListeners.push(callback);
  
  // Call immediately with current state
  callback(authState);
  
  // Return unsubscribe function
  return () => {
    const index = authListeners.indexOf(callback);
    if (index > -1) {
      authListeners.splice(index, 1);
    }
  };
}

// Get auth state
export function getAuthState(): AuthState {
  return authState;
}

// Initialize auth on module load
initializeAuth();
